/**
 * Page Intent Classification Utility
 * 
 * Classifies PropertyPage instances based on their path and title to determine
 * the likely visitor intent. Based on Google search intent classification
 * extended for AI visitor analysis.
 */

export type PageIntent = 
  | 'Homepage'
  | 'Product'
  | 'Pricing'
  | 'Purchase'
  | 'Research'
  | 'Informational'
  | 'Technical'
  | 'Support'
  | 'Trust'
  | 'Corporate'
  | 'Careers'
  | 'Events'
  | 'Authentication'
  | 'Integration'
  | 'Community'
  | 'Legal';

// Available page intents - centralized constant
export const PAGE_INTENTS: readonly PageIntent[] = [
  'Homepage',
  'Product',
  'Pricing',
  'Purchase',
  'Research',
  'Informational',
  'Technical',
  'Support',
  'Trust',
  'Corporate',
  'Careers',
  'Events',
  'Authentication',
  'Integration',
  'Community',
  'Legal'
] as const;

// Default intents for AI Readiness Audit (per PRD Section 9.2)
export const DEFAULT_AUDIT_INTENTS: readonly PageIntent[] = [
  'Homepage',
  'Informational',
  'Product',
  'Pricing',
  'Purchase',
  'Technical',
  'Support',
] as const;

// ═══════════════════════════════════════════════════════════════════════
// Dimension-specific intent groupings
//
// Used by the AI Readiness Audit scorers to determine which dimensions
// apply to which page intents. Centralised here so that intent groupings
// are managed in one place and the scorers stay type-safe.
// ═══════════════════════════════════════════════════════════════════════

/**
 * Intents for which Readability scoring is relevant.
 * Content-heavy pages where Flesch scores are meaningful.
 * Other intents (Product, Homepage, etc.) use fragmented copy that produces
 * misleading readability metrics.
 */
export const READABILITY_APPLICABLE_INTENTS: ReadonlySet<PageIntent> = new Set<PageIntent>([
  'Informational',
  'Research',
  'Technical',
  'Support',
]);

/**
 * Intents for which Grounding coverage recommendations are relevant.
 * These are page types where AI grounding snippet length matters.
 */
export const GROUNDING_RELEVANT_INTENTS: ReadonlySet<PageIntent> = new Set<PageIntent>([
  'Informational',
  'Research',
  'Technical',
  'Support',
  'Product',
  'Homepage',
]);

/**
 * EEAT: Intents where an author byline signal is relevant.
 */
export const EEAT_AUTHOR_RELEVANT_INTENTS: ReadonlySet<PageIntent> = new Set<PageIntent>([
  'Informational',
  'Research',
  'Technical',
  'Support',
]);

/**
 * EEAT: Intents where reviews/testimonials signal is relevant.
 */
export const EEAT_REVIEW_RELEVANT_INTENTS: ReadonlySet<PageIntent> = new Set<PageIntent>([
  'Product',
  'Research',
  'Homepage',
]);

/**
 * EEAT: Intents where external citations signal is relevant.
 */
export const EEAT_CITATION_RELEVANT_INTENTS: ReadonlySet<PageIntent> = new Set<PageIntent>([
  'Informational',
  'Research',
  'Technical',
]);

/**
 * EEAT: Intents where publication/modified dates affect the EEAT score.
 * For other intents, dates are reported as informational findings but
 * do not penalize the score — showing a date on a homepage or product
 * page is not a meaningful trust signal.
 */
export const EEAT_DATE_SCORED_INTENTS: ReadonlySet<PageIntent> = new Set<PageIntent>([
  'Informational',
  'Research',
  'Technical',
  'Support',
]);

/**
 * Citation Readiness: Intents that should be skipped (scored as N/A).
 */
export const CITATION_SKIP_INTENTS: ReadonlySet<PageIntent> = new Set<PageIntent>([
  'Authentication',
  'Legal',
]);

/**
 * Structured Data: Intents that have no schema requirements (scored as N/A).
 */
export const STRUCTURED_DATA_NA_INTENTS: ReadonlySet<PageIntent> = new Set<PageIntent>([
  'Authentication',
  'Integration',
  'Community',
]);

/**
 * AI Citation Pipeline Optimizer: page intents whose full content we crawl +
 * embed for candidate matching. The user picks which page to optimize for a
 * fan-out, and these are the page types that can realistically be the citation
 * target — homepage, product pages, and informational/content pages. Utility,
 * legal, auth, careers, etc. are skipped to keep the one-time crawl cheap and
 * avoid embedding pages that will never be the answer.
 *
 * Note: this matches against the `tags` (PageIntent[]) on PropertyPage, which is
 * what `classifyPageIntent` populates — the `pageType` string column is not used.
 */
export const CITATION_CONTENT_INTENTS: readonly PageIntent[] = [
  'Homepage',
  'Product',
  'Informational',
] as const;

/**
 * Freshness Signals: Intents that get the full freshness check
 * (JSON-LD dates, OG date tags, HTTP Last-Modified, cross-layer consistency).
 * These are content types where recency matters for AI search filtering.
 */
export const FRESHNESS_FULL_CHECK_INTENTS: ReadonlySet<PageIntent> = new Set<PageIntent>([
  'Informational',
  'Research',
  'Technical',
  'Support',
]);

/**
 * Freshness Signals: Intents that get a partial check (JSON-LD dates only).
 * These benefit from dateModified to signal active maintenance, but OG date
 * tags and HTTP Last-Modified are less relevant.
 */
export const FRESHNESS_PARTIAL_CHECK_INTENTS: ReadonlySet<PageIntent> = new Set<PageIntent>([
  'Product',
  'Pricing',
]);

interface IntentRule {
  intent: PageIntent;
  pathPatterns: RegExp[];
  titlePatterns?: RegExp[];
  priority: number; // Higher priority rules are checked first
}

/**
 * Intent classification rules ordered by priority
 * Higher priority rules are checked first to handle overlapping cases
 */
const INTENT_RULES: IntentRule[] = [
  // Homepage - highest priority for root paths
  {
    intent: 'Homepage',
    pathPatterns: [
      /^\/$/,
      /^\/home$/i,
      /^\/index$/i,
    ],
    titlePatterns: [
      /^home$/i,
      /welcome/i,
    ],
    priority: 100,
  },

  // Authentication - high priority to catch before other rules
  {
    intent: 'Authentication',
    pathPatterns: [
      /^\/(login|signin|sign-in|auth|authenticate)(\?.*)?$/i,
      /^\/(register|signup|sign-up|create-account)(\?.*)?$/i,
      /^\/(logout|signout|sign-out)(\?.*)?$/i,
      /^\/auth\//i,
    ],
    titlePatterns: [
      /\b(login|sign in|log in|signin)\b/i,
      /\b(register|sign up|signup|create account)\b/i,
      /\b(logout|sign out|signout)\b/i,
    ],
    priority: 90,
  },

  // Purchase - high priority for conversion pages
  {
    intent: 'Purchase',
    pathPatterns: [
      /^\/(buy|cart|checkout|quote|demo|contact\/sales)(\?.*)?$/i,
      /^\/purchase/i,
      /^\/order/i,
      /^\/payment/i,
      /^\/billing/i,
      /^\/subscribe/i,
      /^\/trial/i,
      /^\/demo/i,
    ],
    titlePatterns: [
      /\b(quote|trial|buy|checkout)\b/i,
      /\btalk to sales\b/i,
      /\bbook.*demo\b/i,
      /\bget started\b/i,
      /\bfree trial\b/i,
      /\bcontact sales\b/i,
    ],
    priority: 85,
  },

  // Pricing - separate from purchase for analysis
  {
    intent: 'Pricing',
    pathPatterns: [
      /^\/(pricing|plans|cost)(\?.*)?$/i,
    ],
    titlePatterns: [
      /^(pricing|plans|cost)/i,
    ],
    priority: 80,
  },

  // Technical Documentation - high priority for dev content
  {
    intent: 'Technical',
    pathPatterns: [
      /^\/(docs|developers|api|sdk|reference|changelog)(\?.*)?$/i,
      /^\/documentation/i,
      /^\/guide/i,
      /^\/tutorial/i,
      /^\/examples/i,
    ],
    titlePatterns: [
      /\b(api|sdk|developer|documentation|docs|reference|changelog)\b/i,
      /```|`[^`]+`/,  // Code blocks or inline code
      /\b(GET|POST|PUT|DELETE|PATCH)\b/,  // HTTP verbs
      /\bjson|xml|curl\b/i,
    ],
    priority: 75,
  },

  // Support - high priority for help content
  {
    intent: 'Support',
    pathPatterns: [
      /^\/(help|support|kb|faq|troubleshoot|contact-support)(\?.*)?$/i,
      /^\/knowledge-base/i,
      /^\/helpdesk/i,
      /^\/contact.*support/i,
    ],
    titlePatterns: [
      /\b(help|support|faq|troubleshoot|knowledge base)\b/i,
      /\bhow to\b/i,
      /\btroubleshooting\b/i,
    ],
    priority: 70,
  },

  // Legal/Trust - high priority for compliance pages
  {
    intent: 'Legal',
    pathPatterns: [
      /^\/(terms|privacy|legal|gdpr|ccpa|cookies)(\?.*)?$/i,
      /^\/terms-of-service/i,
      /^\/privacy-policy/i,
      /^\/cookie-policy/i,
    ],
    titlePatterns: [
      /\b(terms|privacy|legal|gdpr|ccpa|cookies)\b/i,
      /\bterms of service\b/i,
      /\bprivacy policy\b/i,
    ],
    priority: 65,
  },

  // Trust/Security - separate from legal
  {
    intent: 'Trust',
    pathPatterns: [
      /^\/(security|compliance|trust|dpa|certifications)(\?.*)?$/i,
      /^\/security-/i,
      /^\/compliance-/i,
    ],
    titlePatterns: [
      /\b(security|compliance|trust|certification)\b/i,
      /\bSOC 2\b/i,
      /\bISO 27001\b/i,
      /\bHIPAA\b/i,
      /\bGDPR compliant\b/i,
    ],
    priority: 64,
  },

  // Careers - high priority for job-related content
  {
    intent: 'Careers',
    pathPatterns: [
      /^\/(careers|jobs|openings|hiring)(\?.*)?$/i,
      /^\/work-with-us/i,
      /^\/join-us/i,
    ],
    titlePatterns: [
      /\b(careers|jobs|openings|hiring|work with us|join us)\b/i,
    ],
    priority: 60,
  },

  // Events - medium priority
  {
    intent: 'Events',
    pathPatterns: [
      /^\/(events|webinars|recordings|conferences)(\?.*)?$/i,
      /^\/event\//i,
      /^\/webinar/i,
    ],
    titlePatterns: [
      /\b(events|webinar|conference|recording|workshop)\b/i,
    ],
    priority: 55,
  },

  // Integration/Partnerships
  {
    intent: 'Integration',
    pathPatterns: [
      /^\/(integrations|partners|marketplace|connect)(\?.*)?$/i,
      /^\/partner/i,
      /^\/integration/i,
      /^\/app-store/i,
    ],
    titlePatterns: [
      /\b(integration|partner|marketplace|connect|app store)\b/i,
    ],
    priority: 50,
  },

  // Community
  {
    intent: 'Community',
    pathPatterns: [
      /^\/(community|forum|discussion|user-group)(\?.*)?$/i,
      /^\/forums/i,
    ],
    titlePatterns: [
      /\b(community|forum|discussion|user group)\b/i,
    ],
    priority: 45,
  },

  // Research/Comparison - medium priority
  {
    intent: 'Research',
    pathPatterns: [
      /^\/(solutions|industries|compare|vs|alternatives|case-studies|customers)(\?.*)?$/i,
      /^\/solution/i,
      /^\/case-study/i,
      /^\/customer-story/i,
      /^\/testimonial/i,
      /^\/review/i,
    ],
    titlePatterns: [
      /\b(compare|vs|alternative|case study|customer story|testimonial|review)\b/i,
      /\bvs\b|\bversus\b/i,
      /\balternative to\b/i,
    ],
    priority: 40,
  },

  // Product - medium priority (after research to catch specific product pages)
  {
    intent: 'Product',
    pathPatterns: [
      /^\/(product|features|collections|capabilities|what-we-do)(\?.*)?$/i,
      /^\/how-it-works/i,
      /^\/overview/i,
    ],
    titlePatterns: [
      /\b(product|features|capabilities|how it works|overview)\b/i,
    ],
    priority: 35,
  },

  // Corporate - medium priority (includes press releases)
  {
    intent: 'Corporate',
    pathPatterns: [
      /^\/(about|company|team|leadership|investors|press-kit|brand-assets)(\?.*)?$/i,
      /^\/about-us/i,
      /^\/our-team/i,
      /^\/our-story/i,
      /^\/mission/i,
      /^\/vision/i,
      /^\/press-releases(\/|$)/i, // e.g. /press-releases or /press-releases/2024/...
    ],
    titlePatterns: [
      /\b(about|company|team|leadership|mission|vision|our story)\b/i,
    ],
    priority: 30,
  },

  // Informational - lower priority to catch blog/content
  {
    intent: 'Informational',
    pathPatterns: [
      /^\/(blog|guides|resources|news|press|articles|content|how-to)(\?.*)?$/i,
      /^\/blog\//i,
      /^\/guide\//i,
      /^\/resource/i,
      /^\/article/i,
      /^\/post/i,
      /^\/learn/i,
      /^\/education/i,
    ],
    titlePatterns: [
      /\b(blog|guide|resource|article|learn|education|how to|tutorial)\b/i,
    ],
    priority: 20,
  },
];

/**
 * Classifies a page based on its path and title.
 * Path takes precedence: if any rule matches the path, only path-based intents
 * are returned (ordered by priority). This prevents title-only matches (e.g.
 * Support "how to") from overriding path structure (e.g. /blog/... → Informational).
 * If no path rule matches, falls back to path-or-title matching then default Informational.
 *
 * @param path The page path (e.g., "/pricing", "/docs/api")
 * @param title The page title
 * @returns Array of intents that match the page (can be multiple)
 */
export function classifyPageIntent(path: string, title: string): PageIntent[] {
  const normalizedPath = path.toLowerCase();
  const normalizedTitle = title.toLowerCase();

  // Sort rules by priority (highest first)
  const sortedRules = [...INTENT_RULES].sort((a, b) => b.priority - a.priority);

  // Phase 1: collect intents that match by path only
  const pathIntents: PageIntent[] = [];
  for (const rule of sortedRules) {
    for (const pathPattern of rule.pathPatterns) {
      if (pathPattern.test(normalizedPath)) {
        if (!pathIntents.includes(rule.intent)) {
          pathIntents.push(rule.intent);
        }
        break;
      }
    }
  }

  // If at least one path-based intent matched, return only those (path-first)
  if (pathIntents.length > 0) {
    return pathIntents;
  }

  // Phase 2: no path matched — use path or title, then default
  const intents: PageIntent[] = [];
  for (const rule of sortedRules) {
    let matches = false;

    for (const pathPattern of rule.pathPatterns) {
      if (pathPattern.test(normalizedPath)) {
        matches = true;
        break;
      }
    }

    if (!matches && rule.titlePatterns) {
      for (const titlePattern of rule.titlePatterns) {
        if (titlePattern.test(normalizedTitle)) {
          matches = true;
          break;
        }
      }
    }

    if (matches && !intents.includes(rule.intent)) {
      intents.push(rule.intent);
    }
  }

  if (intents.length === 0) {
    intents.push('Informational');
  }

  return intents;
}

/**
 * Classifies a page and returns the primary intent (first match)
 * @param path The page path
 * @param title The page title  
 * @returns The primary intent
 */
export function getPrimaryPageIntent(path: string, title: string): PageIntent {
  const intents = classifyPageIntent(path, title);
  return intents[0];
}

/**
 * Gets all available page intent types
 * @returns Array of all possible page intents
 */
export function getAllPageIntents(): PageIntent[] {
  return [...PAGE_INTENTS];
}

/**
 * Validates if a string is a valid page intent
 * @param intent The intent string to validate
 * @returns True if valid, false otherwise
 */
export function isValidPageIntent(intent: string): intent is PageIntent {
  return getAllPageIntents().includes(intent as PageIntent);
}

